import React, { useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';
import Button from '@/components/atoms/button';
import ConfirmActionModal from '@/components/organisms/confirmActionModal';

const ActionFooter = forwardRef(
	(
		{
			priBtn,
			priBtnVariant,
			priBtnShowIcon,
			priBtnOnClick,
			priBtnDisabled,
			priType,
			extraBtn,
			extraBtnVariant,
			extraBtnShowIcon,
			extraBtnOnClick,
			extraBtnDisabled,
			extraBtnType,
			secBtn,
			secBtnVariant,
			secBtnShowIcon,
			secBtnOnClick,
			secBtnDisabled,
			secBtnType,
			classes,
			wrapperClasses,
			hidePrimary,
			confirmSecBtnClick,
			...props
		},
		ref
	) => {
		const confirmSecBtnClickRef = useRef();

		return (
			<>
				<div ref={ref} className={cn('fixed bottom-0 right-0 w-full bg-white border-t border-formOutline', wrapperClasses)}>
					<div className={cn('flex justify-end gap-2 px-6 py-4 mx-auto', classes)}>
						{secBtn && (
							<Button
								variant={secBtnVariant}
								disabled={secBtnDisabled}
								type={secBtnType}
								onClick={(e) => {
									if (!confirmSecBtnClick) {
										secBtnOnClick();
										return;
									}

									if (confirmSecBtnClickRef?.current) confirmSecBtnClickRef.current.onOpen();
								}}
								{...(secBtnShowIcon && { icon: 'plus' })}
							>
								{secBtn}
							</Button>
						)}

						{extraBtn && (
							<Button
								variant={extraBtnVariant}
								onClick={extraBtnOnClick}
								disabled={extraBtnDisabled}
								type={extraBtnType}
								{...(extraBtnShowIcon && { icon: 'plus' })}
							>
								{extraBtn}
							</Button>
						)}
						{!hidePrimary && 
							<Button variant={priBtnVariant} onClick={priBtnOnClick} disabled={priBtnDisabled} type={priType} {...(priBtnShowIcon && { icon: 'plus' })}>
								{priBtn}
							</Button>
	}
					</div>
				</div>
				{confirmSecBtnClick && (
					<ConfirmActionModal
						ref={confirmSecBtnClickRef}
						contents="Are you sure you want to cancel? Any unsaved changes will be lost."
						actionBtnText="Cancel"
						cancelBtnText="Ok"
						confirmingBtnText="Cancelling..."
						icon="warning"
						iconClassName="text-notificationsWarning"
						isContentNarrow
						reverseBtnOrder
						onConfirm={(handleConfirmModalClose) => {
							// close the confirmation modal
							handleConfirmModalClose();

							// reset the form
							secBtnOnClick();
						}}
					/>
				)}
			</>
		);
	}
);

export default ActionFooter;

ActionFooter.propTypes = {
	priBtn: PropTypes.string.isRequired,
	priBtnVariant: PropTypes.oneOf([
		'primary',
		'primary-white',
		'secondary',
		'secondary-white',
		'icon',
		'circle-icon',
		'small-button',
		'underline',
		'nav-button',
		'nav-top-button',
		'outline',
		'ghost',
	]),
	priBtnShowIcon: PropTypes.bool,
	priBtnOnClick: PropTypes.func,
	priBtnDisabled: PropTypes.bool,
	priType: PropTypes.string,
	extraBtn: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	extraBtnVariant: PropTypes.oneOf([
		'primary',
		'primary-white',
		'secondary',
		'secondary-white',
		'icon',
		'circle-icon',
		'small-button',
		'underline',
		'nav-button',
		'nav-top-button',
		'outline',
		'ghost',
	]),
	extraBtnShowIcon: PropTypes.bool,
	extraBtnOnClick: PropTypes.func,
	extraBtnDisabled: PropTypes.bool,
	extraBtnType: PropTypes.string,
	secBtn: PropTypes.string,
	secBtnVariant: PropTypes.oneOf([
		'primary',
		'primary-white',
		'secondary',
		'secondary-white',
		'icon',
		'circle-icon',
		'small-button',
		'underline',
		'nav-button',
		'nav-top-button',
		'outline',
		'ghost',
	]),
	secBtnShowIcon: PropTypes.bool,
	secBtnOnClick: PropTypes.func,
	secBtnDisabled: PropTypes.bool,
	secBtnType: PropTypes.string,
	confirmSecBtnClick: PropTypes.bool,
};

ActionFooter.defaultProps = {
	priBtn: null,
	priBtnVariant: 'primary',
	hidePrimary: false,
	priBtnShowIcon: true,
	priBtnOnClick: () => {},
	priBtnDisabled: false,
	extraBtn: null,
	extraBtnVariant: 'primary',
	extraBtnShowIcon: true,
	extraBtnDisabled: false,
	extraBtnOnClick: () => {},
	secBtn: null,
	secBtnVariant: 'secondary',
	secBtnShowIcon: true,
	secBtnDisabled: false,
	secBtnOnClick: () => {},
	confirmSecBtnClick: false,
};
